"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MetricsClient = void 0;
var constants_1 = require("./constants");
var LoggerFactory_1 = require("./utils/LoggerFactory");
/**
 * The Metrics class provides a way to monitor events within an app by
 * providing a means of reporting various types of events to various
 * destinations. This class can be used directly, but the recommended
 * use is to use the MetricsProvider and useMetrics hook.
 */
var MetricsClient = /** @class */ (function () {
    function MetricsClient(reporters, baseData) {
        if (reporters === void 0) { reporters = []; }
        // Check if we should enable the logger debug mode
        var _a = baseData || {}, environmentName = _a.environmentName, environment = _a.environment;
        if (environmentName == 'dev' || environment == 'dev') {
            (0, LoggerFactory_1.enableDebugMode)();
        }
        this.reporters = reporters;
        this.baseData = baseData;
    }
    /**
     * Sends an event with some optional data to each reporter.
     * @param {*} type the type of event
     * @param {*} data An object to send with the event (be sure the data is accepted by the reports being used)
     * @param {*} options Additional options that will be sent to the reporter
     */
    MetricsClient.prototype.trackEvent = function (type, data, options, callback) {
        var dataCopy = this.baseData ? __assign(__assign({}, this.baseData), (data || {})) : data;
        // serverDuration is deprecated so we won't send it anymore
        var _a = dataCopy || {}, _b = _a.duration, duration = _b === void 0 ? 0 : _b, _c = _a.serverDuration, serverDuration = _c === void 0 ? 0 : _c, eventData = __rest(_a, ["duration", "serverDuration"]);
        var finalData = eventData
            ? __assign(__assign({}, eventData), { type: (data === null || data === void 0 ? void 0 : data.type) || 'tracked', duration: duration > 0 ? duration : undefined }) : undefined;
        // Clean internal data set by the api
        delete finalData['skip'];
        delete finalData['expectedError'];
        delete finalData['expected_error'];
        delete finalData['error'];
        delete finalData['incompleteMeasure'];
        delete finalData['incomplete_measure'];
        delete finalData['error'];
        this.reporters.forEach(function (reporter) { return reporter(type, finalData, options, callback); });
    };
    /**
     * Starts measuring an event. To complete the measurement and send the event to the reporters
     * call record, recordSuccess, or recordError. In addition to data specified in the initial call
     * calls to update, and record call. A duration and result entry are added to data when being
     * reported.
     *
     * @param {*} type the type of event
     * @param initialData Record with a key and value
     * @param {*} options Additional options that will be sent to the reporter
     */
    MetricsClient.prototype.measureEvent = function (type, initialData, options, callback) {
        var _this = this;
        var start = new Date();
        var data = __assign({}, initialData);
        var eventData = this.baseData;
        var updateData = function (updateFnOrData) {
            if (updateFnOrData) {
                if (typeof updateFnOrData === 'function') {
                    data = updateFnOrData(data);
                }
                else {
                    data = __assign(__assign({}, data), updateFnOrData);
                }
            }
        };
        var record = function (result, updateFnOrData, moreOptions) {
            var duration = new Date().valueOf() - start.valueOf();
            // only send serverDuration if the type is a page_load
            var serverDuration = type !== constants_1.METRIC_TYPE.PAGE_LOAD ? undefined : (data === null || data === void 0 ? void 0 : data.serverDuration) || (eventData === null || eventData === void 0 ? void 0 : eventData.serverDuration) || undefined;
            updateData({
                duration: duration,
                result: result
            });
            updateData(updateFnOrData);
            if (!data.skip) {
                _this.trackEvent(type, __assign(__assign({}, data), { serverDuration: serverDuration }), __assign(__assign({}, options), moreOptions), callback);
            }
        };
        return {
            // The update function allows the data related to an event to be updated
            // asynchronously as the event is occurring. An example use case is a counter.
            update: function (updateFnOrData) { return updateData(updateFnOrData); },
            // The record function sends the event to the reporters, it must be passed a
            // result to be recorded along with duration.
            record: record,
            // Two helper functions are provided that just call record with a set result
            recordSuccess: function (updateFnOrData) {
                record('success', updateFnOrData);
            },
            recordError: function (updateFnOrData) { return record('error', updateFnOrData); },
            getData: function () { return data; }
        };
    };
    /**
     * This will measure how long a function takes to complete, and it results in an error
     * or a normal return. The function can be async or normal. To use it pass it the function
     * to measure and use the returned function as you would the measured function.
     *
     * Example usage:
     * const foo = measureFunction(async () => client.query(), 'query', { query: 'detail_query });
     * foo(arg1);
     *
     * @param {*} func the function to measure
     * @param {*} type the type of event
     * @param {*} data An object to send with the event (be sure the data is accepted by the reports being used)
     * @param {*} options Additional options that will be sent to the reporter
     */
    MetricsClient.prototype.measureFunction = function (func, type, data, options) {
        var _this = this;
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var _a = _this.measureEvent(type, data, options), recordSuccess = _a.recordSuccess, recordError = _a.recordError;
            try {
                var result = func.apply(void 0, args);
                if (Boolean(result && typeof result.then === 'function')) {
                    return result
                        .then(function (resultValue) {
                        recordSuccess();
                        return resultValue;
                    })
                        .catch(function (error) {
                        recordError();
                        throw error;
                    });
                }
                recordSuccess();
                return result;
            }
            catch (error) {
                recordError();
                throw error;
            }
        };
    };
    return MetricsClient;
}());
exports.MetricsClient = MetricsClient;
